import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import {
  clearReconciliationRunDetail,
  fetchReconciliationRunDetail,
  fetchReconciliationRunExportJson,
} from '../../redux/modules/reconciliation/reconciliation.actions';
import {
  reconciliationRunDetailLoadingSelector,
  reconciliationRunExportLoadingSelector,
  reconciliationSelector,
} from '../../redux/modules/reconciliation/reconciliation.selectors';
import { useAppDispatch } from '../../redux/hooks';
import { ReconciliationRunItem } from '../../types/reconciliation';
import { formatDateTime } from '../../utils/date';
import {
  downloadReconciliationRunCsvFromJson,
  downloadReconciliationRunXlsxFromJson,
} from '../../utils/reconciliationExport';

function formatItemStatus(status: string) {
  if (status === 'MATCH') {
    return 'Match';
  }
  if (status === 'MISMATCH') {
    return 'No match';
  }
  return status;
}

interface ReconciliationRunDetailViewProps {
  runId: number;
  onBack: () => void;
  onViewComparison: (itemId: number) => void;
}

export function ReconciliationRunDetailView({ runId, onBack, onViewComparison }: ReconciliationRunDetailViewProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const detailLoading = useSelector(reconciliationRunDetailLoadingSelector);
  const exportLoading = useSelector(reconciliationRunExportLoadingSelector);
  const { activeRunDetail, runDetailItems, runDetailTotalItems, fetchRunDetailStatus, runDetailError } =
    useSelector(reconciliationSelector);

  const handleExportCsv = useCallback(async () => {
    try {
      const data = await dispatch(fetchReconciliationRunExportJson(runId)).unwrap();
      downloadReconciliationRunCsvFromJson(data);
      enqueueSnackbar('CSV downloaded.', { variant: 'success' });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Failed to export CSV';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [dispatch, enqueueSnackbar, runId]);

  const handleExportXlsx = useCallback(async () => {
    try {
      const data = await dispatch(fetchReconciliationRunExportJson(runId)).unwrap();
      downloadReconciliationRunXlsxFromJson(data);
      enqueueSnackbar('Excel file downloaded.', { variant: 'success' });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'message' in err && typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Failed to export Excel';
      enqueueSnackbar(message, { variant: 'error' });
    }
  }, [dispatch, enqueueSnackbar, runId]);

  useEffect(() => {
    dispatch(fetchReconciliationRunDetail(runId));

    return () => {
      dispatch(clearReconciliationRunDetail());
    };
  }, [dispatch, runId]);

  const itemColumns = useMemo<GridColDef<ReconciliationRunItem>[]>(
    () => [
      {
        field: 'id',
        headerName: 'Item ID',
        width: 110,
      },
      {
        field: 'fundDisplay',
        headerName: 'Fund',
        width: 220,
        sortable: false,
        filterable: false,
        valueGetter: (params) => {
          const name = params.row.fundName?.trim();
          const code = params.row.fundId;
          if (name && code) {
            return `${name} (${code})`;
          }
          return name || code || '—';
        },
      },
      {
        field: 'itemStatus',
        headerName: 'Item status',
        width: 140,
        valueGetter: (params) => formatItemStatus(params.row.itemStatus),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        width: 150,
        renderCell: (params: GridRenderCellParams<ReconciliationRunItem>) => (
          <Button size="small" variant="text" onClick={() => onViewComparison(params.row.id)}>
            View comparison
          </Button>
        ),
      },
    ],
    [onViewComparison],
  );

  const showSpinner =
    detailLoading || (fetchRunDetailStatus === fetchStatuses.pending && !activeRunDetail && !runDetailError?.message);

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} variant="outlined" size="small">
          Back to runs
        </Button>
      </Box>

      {runDetailError?.message && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {runDetailError.message}
        </Alert>
      )}

      {showSpinner && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {activeRunDetail && (
        <>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight={600}>
              Run summary
            </Typography>
            <Stack spacing={0.75}>
              <Typography variant="body2">Run ID: {activeRunDetail.id}</Typography>
              <Typography variant="body2">Run date: {formatDateTime(activeRunDetail.createdAt)}</Typography>
              <Typography variant="body2">Run type: {activeRunDetail.runType}</Typography>
              <Typography variant="body2">Phase: {activeRunDetail.phase}</Typography>
              <Typography variant="body2">Status: {activeRunDetail.status}</Typography>
              {activeRunDetail.executionFileName ? (
                <Typography variant="body2">Execution file: {activeRunDetail.executionFileName}</Typography>
              ) : null}
              {activeRunDetail.executionFileUploadedAt ? (
                <Typography variant="body2">
                  <strong>File uploaded at:</strong> {formatDateTime(activeRunDetail.executionFileUploadedAt)}
                </Typography>
              ) : null}
            </Stack>
          </Paper>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            {runDetailTotalItems > runDetailItems.length ? (
              <Typography variant="caption" color="text.secondary">
                Showing {runDetailItems.length} of {runDetailTotalItems} items.
              </Typography>
            ) : (
              <Typography variant="subtitle2" component="span">
                Items
              </Typography>
            )}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <LoadingButton
                type="button"
                color="primary"
                variant="outlined"
                size="small"
                endIcon={<DownloadIcon />}
                loading={exportLoading}
                loadingPosition="end"
                onClick={handleExportCsv}
              >
                Export CSV
              </LoadingButton>
              <LoadingButton
                type="button"
                color="primary"
                variant="outlined"
                size="small"
                endIcon={<DownloadIcon />}
                loading={exportLoading}
                loadingPosition="end"
                onClick={handleExportXlsx}
              >
                Export Excel
              </LoadingButton>
            </Stack>
          </Box>

          <Table
            hideToolbar
            rows={runDetailItems}
            columns={itemColumns}
            getRowId={(row) => row.id}
            initialPageSize={10}
          />
        </>
      )}
    </Box>
  );
}
